import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { get as _get, includes as _includes, some as _some } from 'lodash';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, take, takeUntil } from 'rxjs/operators';

import { BillAccountActions, BillAccountsSelectors } from '@amfam/billing/billaccount/data-access';
import { BillAccount, UpdatePreferencesRequest } from '@amfam/shared/models';
import { ImpersonateRolesService } from '@amfam/shared/utility/impersonation';
import { CopyService, UtilService } from '@amfam/shared/utility/shared-services';
import { ToasterService, ValidationService } from '@amfam/ui-kit';

import { selectFilteredBillAccountNotification } from '../../+state/selectors';

@Component({
  selector: 'ds-billing-online-preference',
  templateUrl: './billing-online-preference.component.html',
  standalone: false
})
export class BillingOnlinePreferenceComponent implements OnInit, OnDestroy, OnChanges {
  @Input()
  public billAccount: BillAccount;

  @Input()
  private hasAutopayEnabled = false;

  public editOLBForm: UntypedFormGroup;
  public accountName: AbstractControl;
  public reminder: AbstractControl;
  // This needs to be a string array since the backend is bringing this value across as a string.
  public reminderOptions = [7, 6, 5, 4, 3];
  dataLoading: Observable<boolean>;
  updatingPreferences: Observable<boolean>;
  saveBtnContent: string;
  updateCorrelationId = this.utilService.generateId();

  billAccountNotification$ = this.store.select(
    selectFilteredBillAccountNotification(this.updateCorrelationId)
  );

  private reminderDate: string;
  private billingAccountName: string;
  private billAccNickNamesList: Array<string> = [];

  // Shared subject for completing observables
  private stop$: Subject<void> = new Subject<void>();

  private billAccounts = [];
  private billAccountsAnyLoading$: Observable<boolean>;
  private billAccountsLoading$: Observable<boolean>;

  constructor(
    private store: Store,
    private formBuilder: UntypedFormBuilder,
    private toasterService: ToasterService,
    public roleService: ImpersonateRolesService,
    private utilService: UtilService,
    private copyService: CopyService
  ) {}

  ngOnInit() {
    this.saveBtnContent = this.copyService.getCopy('shared.saveBtn');

    this.store.dispatch(
      BillAccountActions.billAccountUpdatePreferenceInit(this.billAccount.billAccountNumber)
    );

    this.billAccountsAnyLoading$ = this.store.select(
      BillAccountsSelectors.selectBillAccountsAnyLoading
    );
    this.billAccountsLoading$ = this.store.select(BillAccountsSelectors.selectBillAccountsLoading);

    // Data is served from the bill accounts store.
    this.dataLoading = combineLatest([
      this.billAccountsAnyLoading$,
      this.billAccountsLoading$
    ]).pipe(
      map(
        ([billAccountsAnyLoading, billAccountsLoading]) =>
          billAccountsAnyLoading || billAccountsLoading
      )
    );

    // An update preferences action is in flight
    this.updatingPreferences = this.store.select(
      BillAccountsSelectors.selectBillAccountsUpdatingPreferences
    );

    this.store
      .select(BillAccountsSelectors.selectBillAccounts)
      .pipe(
        filter(billAccounts => !_some(billAccounts, { loading: true })),
        takeUntil(this.stop$)
      )
      .subscribe(billAccounts => {
        this.billAccounts = billAccounts;
      });

    this.setupForm();
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }

  ngOnChanges() {
    this.setupForm();
  }

  resetForm() {
    this.accountName.reset(this.billingAccountName);
    this.accountName.updateValueAndValidity();
    this.reminder.reset(this.reminderDate);
    this.reminder.updateValueAndValidity();
  }

  // Submit
  updateBillingPreferences() {
    // build payload
    const updateBillingPreferencePayload: UpdatePreferencesRequest = {
      billAccountNumber: this.billAccount.billAccountNumber,
      updatePreferenceCorrelationId: this.updateCorrelationId,
      associated: this.billAccount.associated,
      billingMethod: this.billAccount.billingMethod,
      payload: {
        billingPreference: {
          accountNickname: this.accountName.value,
          dueDateReminder: this.getReminderValue(),
          preferences: _get(this.billAccount, 'billingPreferences.preferences')
        }
      }
    };
    this.store.dispatch(
      BillAccountActions.billAccountUpdateBillingPreference(updateBillingPreferencePayload)
    );

    this.billAccountNotification$
      .pipe(filter(Boolean), take(1))
      .subscribe(matchingBillAccountNotification => {
        if (
          _get(
            matchingBillAccountNotification,
            'updatedBillingPreferences',
            matchingBillAccountNotification.updatedPreferences
          )
        ) {
          this.toasterService.pop('success', 'Your preferences have been updated');
          this.editOLBForm.markAsPristine();
        } else if (
          _get(
            matchingBillAccountNotification,
            'updateBillingPreferencesError',
            matchingBillAccountNotification.updatePreferencesError
          )
        ) {
          this.toasterService.pop('error', 'There was an error saving your preferences');
        }

        this.store.dispatch(
          BillAccountActions.billAccountUpdateBillingPreferenceInit(
            this.billAccount.billAccountNumber
          )
        );
      });
  }

  private determineNickNameRestrictions() {
    this.billAccNickNamesList = [];
    this.billAccounts.forEach(ba => {
      if (
        _get(ba, 'billingPreferences.accountNickName') &&
        ba.billAccountNumber !== this.billAccount.billAccountNumber
      ) {
        this.billAccNickNamesList.push(ba.billingPreferences.accountNickName.toLowerCase());
      }
    });
  }

  private setupForm() {
    // Derive the list of nicknames that are not allowed
    this.determineNickNameRestrictions();

    this.billingAccountName = _get(this.billAccount, 'billingPreferences.accountNickName', '');

    this.reminderDate = String(_get(this.billAccount, 'billingPreferences.dueDateReminder', '7'));

    // The currently selected reminder value can be set externally to an option not listed by default
    if (!_includes(this.reminderOptions, Number(this.reminderDate)) && this.reminderDate !== '-1') {
      this.reminderOptions.push(Number(this.reminderDate));
    }

    this.editOLBForm = this.formBuilder.group(
      {
        accountName: [
          this.billingAccountName,
          Validators.compose([
            Validators.required,
            Validators.maxLength(25),
            ValidationService.nickNameValidator,
            ValidationService.nickNameNumberLengthValidator
          ])
        ],
        reminder: [this.reminderDate, Validators.compose([Validators.required])]
      },
      {
        validator: Validators.compose([
          ValidationService.nickNameDuplicationValidator('accountName', this.billAccNickNamesList)
        ])
      }
    );
    this.accountName = this.editOLBForm.controls['accountName'];
    this.reminder = this.editOLBForm.controls['reminder'];
  }

  private getReminderValue() {
    return this.reminder.value === 'No Reminder' ? '-1' : <string>this.reminder.value;
  }
}
